<template>
  <div :class="{ 'admin-verification-table': adminMode }">
    <div v-if="adminMode" class="filter-section">
      <div class="columns is-multiline">
        <div class="column is-3">
          <b-field label="Organization">
            <b-select
              v-model="filters.organization_id"
              aria-label="Organization"
              expanded
              @input="changeOrganization"
            >
              <option value="">All Organizations</option>
              <option v-for="org in organizations" :key="org.id" :value="org.id">
                {{ org.name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div v-if="!isRestricted || adminMode" class="column is-3">
          <b-field label="User">
            <b-select
              v-model="filters.user_id"
              aria-label="User"
              expanded
              :disabled="adminMode && !filters.organization_id"
              @input="reload"
            >
              <option value="">All Users</option>
              <option v-for="user in users" :key="user.id" :value="user.id">
                {{ user.first_name }} {{ user.last_name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="column is-3">
          <b-field label="Source">
            <b-select v-model="filters.result_source" aria-label="Source" expanded @input="reload">
              <option value="">All Sources</option>
              <option value="provider">Provider</option>
              <option value="saved">Saved response</option>
            </b-select>
          </b-field>
        </div>
      </div>
    </div>
    <b-field class="search-section">
      <div class="field-body">
        <b-input
          class="is-expanded"
          :loading="loading"
          v-model="search"
          placeholder="Search by first or last name, or DOB (MM/DD/YYYY)"
          @input="emitSearchTerm"
        />
        <b-dropdown
          class="filter-dropdown"
          position="is-bottom-left"
          v-model="searchableFields"
          multiple
          aria-role="list"
          @input="reload"
        >
          <template #trigger>
            <b-button icon-right="filter" type="is-primary" class="filter-btn">
              <span class="filter-btn-text">
                Search Fields
                <span class="filter-count">({{ searchableFields.length }})</span>
              </span>
            </b-button>
          </template>
          <b-dropdown-item
            v-for="field in allFields"
            :key="field.value"
            :value="field.value"
            aria-role="listitem"
          >
            <b-checkbox :value="searchableFields.includes(field.value)" class="checkbox-left">
              {{ field.label }}
            </b-checkbox>
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </b-field>
    <b-message v-if="error" type="is-danger" :closable="false">{{ error }}</b-message>
    <b-table
      :data="rows"
      :loading="loading"
      striped
      hoverable
      backend-sorting
      :default-sort="['created_at', 'desc']"
      @sort="sort"
    >
      <b-table-column field="patient_last_name" label="Patient" sortable v-slot="props">
        <router-link
          :to="{
            name: adminMode ? 'AdminPatientVerificationDetail' : 'PatientVerificationDetail',
            params: { id: props.row.id },
          }"
        >
          {{ props.row.patient_first_name }} {{ props.row.patient_last_name }}
        </router-link>
      </b-table-column>
      <b-table-column field="dob" label="DOB" v-slot="props">
        {{ props.row.dob || '—' }}
      </b-table-column>
      <b-table-column field="user" label="User" v-slot="props">{{ props.row.user }}</b-table-column>
      <b-table-column v-if="adminMode" field="organization" label="Organization" v-slot="props">
        {{ props.row.organization }}
      </b-table-column>
      <b-table-column field="created_at" label="Date" sortable v-slot="props">
        {{ props.row.created_at | formatDate }}
      </b-table-column>
      <b-table-column field="patients_found" label="Matches" v-slot="props">
        {{
          props.row.status === 'complete'
            ? props.row.patients_found
            : props.row.status === 'no_match'
              ? 0
              : '—'
        }}
      </b-table-column>
      <b-table-column v-if="adminMode" field="result_source" label="Source" v-slot="props">
        {{ props.row.result_source === 'saved' ? 'Saved response' : 'Provider' }}
      </b-table-column>
      <b-table-column field="status" label="Status" v-slot="props">
        <b-tag
          :type="
            props.row.status === 'complete'
              ? 'is-success'
              : props.row.status === 'error'
                ? 'is-danger'
                : 'is-warning'
          "
        >
          {{ props.row.status === 'no_match' ? 'No match' : props.row.status }}
        </b-tag>
      </b-table-column>
      <template #empty>
        <p class="has-text-centered p-4">No patient verifications found.</p>
      </template>
    </b-table>
    <b-pagination
      class="mt-4"
      :total="total"
      :per-page="15"
      :current.sync="page"
      @change="fetchRows"
    />
  </div>
</template>
<script>
import request from '../request';
import { mapGetters } from 'vuex';
import { sanitizeSearchInput } from '../utils/form';
export default {
  name: 'PatientVerificationTable',
  props: {
    adminMode: { type: Boolean, default: false },
    userId: { type: [String, Number], default: '' },
  },
  data() {
    return {
      rows: [],
      search: '',
      searchableFields: ['patient_first_name', 'patient_last_name', 'dob'],
      allFields: [
        { value: 'patient_first_name', label: 'First Name' },
        { value: 'patient_last_name', label: 'Last Name' },
        { value: 'dob', label: 'Birth Date' },
      ],
      users: [],
      organizations: [],
      loading: false,
      error: '',
      page: 1,
      total: 0,
      timer: null,
      sequence: 0,
      filters: {
        organization_id: '',
        user_id: '',
        result_source: '',
        sort_field: 'created_at',
        sort_order: 'desc',
      },
    };
  },
  computed: { ...mapGetters(['isRestricted']) },
  watch: {
    userId() {
      this.reload();
    },
  },
  mounted() {
    this.fetchRows();
    this.loadFilters();
  },
  beforeDestroy() {
    clearTimeout(this.timer);
    this.sequence++;
  },
  methods: {
    emitSearchTerm() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.reload(), 600);
    },
    reload() {
      clearTimeout(this.timer);
      this.page = 1;
      this.fetchRows();
    },
    sort(field, order) {
      this.filters.sort_field = field;
      this.filters.sort_order = order;
      this.reload();
    },
    async fetchRows() {
      const sequence = ++this.sequence;
      this.loading = true;
      this.error = '';
      const params = { page: this.page, limit: 15 };
      Object.entries(this.filters).forEach(([key, value]) => {
        if (key === 'result_source' && !this.adminMode) return;
        if (value !== '') params[key] = value;
      });
      if (!this.adminMode) {
        delete params.user_id;
        if (this.userId && !this.isRestricted) params.user_id = this.userId;
      }
      const search = sanitizeSearchInput(this.search);
      if (search)
        this.searchableFields.forEach((field) => {
          params[field] = search;
        });
      try {
        const resp = await request.get(`${this.adminMode ? '/admin' : ''}/patient-verifications`, {
          params,
        });
        if (sequence !== this.sequence) return;
        this.rows = resp.data.list;
        this.total = Number(resp.headers['x-total-count'] || 0);
      } catch (err) {
        if (sequence === this.sequence) this.error = 'Unable to load verification history.';
      } finally {
        if (sequence === this.sequence) this.loading = false;
      }
    },
    async loadFilters() {
      try {
        if (this.adminMode) {
          const resp = await request.get('/admin/organizations');
          this.organizations = resp.data.organizations;
        }
      } catch (err) {
        this.error = 'Unable to load history filters.';
      }
    },
    async changeOrganization() {
      this.filters.user_id = '';
      this.users = [];
      this.reload();
      if (!this.filters.organization_id) return;
      try {
        const resp = await request.get('/admin/users', {
          params: { organization_id: this.filters.organization_id },
        });
        this.users = resp.data.users;
      } catch (err) {
        this.error = 'Unable to load organization users.';
      }
    },
  },
};
</script>

<style scoped>
.filter-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f5f5f5;
  border-radius: 4px;
}
.admin-verification-table .search-section {
  margin-bottom: 1rem;
}
.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
}
.filter-dropdown {
  margin-left: 10px;
}
.filter-dropdown .dropdown-item {
  color: black !important;
}
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}
.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
  pointer-events: none;
}
.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}
@media (max-width: 768px) {
  .field-body {
    gap: 0.75rem;
  }
  .field-body .is-expanded {
    flex: 1;
    min-width: 0;
  }
  .field-body .filter-dropdown {
    flex-shrink: 0;
    margin-left: 0;
  }
  .field-body .filter-dropdown :deep(.button) {
    padding: 0.4em 0.75em;
    height: 2.35em;
    font-size: 14px;
  }
  .filter-count {
    display: none;
  }
}
@media (max-width: 375px) {
  .field-body {
    flex-direction: column;
    align-items: stretch;
  }
  .field-body .filter-dropdown,
  .field-body .filter-dropdown :deep(.dropdown-trigger),
  .field-body .filter-dropdown :deep(.button) {
    width: 100%;
  }
}
</style>
